import { Alert, AlertColor, Snackbar } from '@mui/material';
import React, { createContext, useState } from 'react';

export interface Alert {
  message: string;
  severity?: AlertColor;
  duration?: number;
  dismissible?: boolean;
}

export interface AlertParams {
  open: boolean;
  alert: Alert;
  setAlert: (alert: Alert) => void;
  clearAlert: () => void;
}

export const AlertContext = createContext<AlertParams>({
  open: false,
  alert: {
    message: '',
    severity: 'success',
    dismissible: true,
  },
  setAlert: () => {},
  clearAlert: () => {},
});

export const AlertProvider = ({ children }: { children: React.ReactNode }) => {
  const [alert, setAlert] = useState<Alert>({
    message: '',
    severity: 'success',
    dismissible: true,
    duration: 4000,
  });
  const [open, setOpen] = useState<boolean>(false);

  const clearAlert = (): void => {
    setOpen(false);
  };

  const updateAlert = (alert: Alert) => {
    const { message, severity, duration, dismissible } = alert;
    setOpen(true);
    setAlert({
      message,
      severity: severity || 'error',
      duration: duration || 4000, // defaults to 4 seconds
      dismissible: dismissible || true, // the 'X' will appear by default
    });
  };

  return (
    <AlertContext.Provider
      value={{
        open,
        alert,
        setAlert: updateAlert,
        clearAlert,
      }}
    >
      {children}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open}
        autoHideDuration={alert.duration}
        onClose={clearAlert}
      >
        <Alert
          onClose={alert.dismissible ? () => setOpen(false) : undefined}
          severity={alert.severity}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
};
